"use client";

import React, { useState } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Plus, 
  Minus,
  Heart, 
  Search, 
  Filter, 
  ChevronRight,
  Package,
  ShoppingCart,
  X,
  CreditCard,
  Lock
} from "lucide-react";
import { useShoppingCart } from "@/components/ui/shopping-cart";
import { createCheckoutSession } from "@/lib/polar";

// Types for our storefront
interface PolarProduct {
  id: string;
  name: string;
  description?: string;
  price: number;
  category: string;
  image: string;
  images?: string[];
  color: string;
  colors?: string[];
  sizes?: string[];
  inStock?: boolean;
  featured?: boolean;
  rating?: number;
}

interface CartItem extends PolarProduct {
  quantity: number;
  selectedColor?: string;
  selectedSize?: string;
}

interface StorefrontComponentProps {
  products?: PolarProduct[];
  currency?: string;
  onCheckout?: (items: CartItem[]) => void;
}

export function StorefrontComponent({
  products = [],
  currency = "$",
  onCheckout
}: StorefrontComponentProps) {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [activeView, setActiveView] = useState<"grid" | "detail">("grid");
  const [selectedProduct, setSelectedProduct] = useState<PolarProduct | null>(null);
  const [selectedColor, setSelectedColor] = useState<string>("");
  const [selectedSize, setSelectedSize] = useState<string>("");
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isCheckoutProcessing, setIsCheckoutProcessing] = useState(false);

  // Calculate totals
  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  // Format price in dollars and cents
  const formatPrice = (price: number) => {
    const dollars = Math.floor(price / 100);
    const cents = price % 100;
    return `${currency}${dollars}${cents ? `.${cents.toString().padStart(2, '0')}` : ''}`;
  };

  // Filter products based on search and filters
  const filteredProducts = products.filter(product => {
    const matchesSearch = searchQuery === "" || 
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.category.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilters = activeFilters.length === 0 || 
      activeFilters.includes(product.category);
    
    return matchesSearch && matchesFilters;
  });

  // Get unique categories for filters
  const categories = [...new Set(products.map(product => product.category))];

  // Cart functions
  const addToCart = (product: PolarProduct, quantity = 1) => {
    setCart(currentCart => {
      const existingItem = currentCart.find(item => 
        item.id === product.id && 
        item.selectedColor === (selectedColor || product.color) &&
        item.selectedSize === selectedSize
      );
      
      if (existingItem) {
        return currentCart.map(item =>
          item.id === product.id && 
          item.selectedColor === (selectedColor || product.color) &&
          item.selectedSize === selectedSize
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      
      return [...currentCart, { 
        ...product, 
        quantity, 
        selectedColor: selectedColor || product.color,
        selectedSize
      }];
    });
    
    setIsCartOpen(true);
  };

  const removeFromCart = (itemId: string, color?: string, size?: string) => {
    setCart(currentCart =>
      currentCart.filter(item => 
        !(item.id === itemId && 
          item.selectedColor === color && 
          item.selectedSize === size)
      )
    );
  };

  const updateQuantity = (itemId: string, delta: number, color?: string, size?: string) => {
    setCart(currentCart =>
      currentCart.map(item => {
        if (item.id === itemId && 
            item.selectedColor === color && 
            item.selectedSize === size) {
          const newQuantity = item.quantity + delta;
          if (newQuantity <= 0) {
            return item; // Will be removed by the next filter
          }
          return { ...item, quantity: newQuantity };
        }
        return item;
      }).filter(item => item.quantity > 0)
    );
  };

  // View product details
  const viewProductDetails = (product: PolarProduct) => {
    setSelectedProduct(product);
    setSelectedColor(product.color);
    setSelectedSize(product.sizes?.[0] || "");
    setActiveView("detail");
  };

  // Back to grid
  const backToGrid = () => {
    setActiveView("grid");
    setSelectedProduct(null);
  };

  // Toggle filter
  const toggleFilter = (category: string) => {
    setActiveFilters(current => 
      current.includes(category)
        ? current.filter(c => c !== category)
        : [...current, category]
    );
  };

  // Handle checkout
  const handleCheckout = async () => {
    setIsCheckoutProcessing(true);
    
    try {
      if (cart.length === 0) return;
      
      // Since we're dealing with different products, we'll just use the first item's ID
      // In a real implementation, you'd create a bundle checkout or handle multiple items
      const firstItem = cart[0];
      
      const checkoutUrl = await createCheckoutSession({
        productId: firstItem.id,
        successUrl: `${window.location.origin}/store?success=true`,
        cancelUrl: `${window.location.origin}/store?canceled=true`,
      });
      
      // Redirect to checkout URL
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      }
      
      if (onCheckout) {
        onCheckout(cart);
      }
    } catch (error) {
      console.error("Checkout error:", error);
    } finally {
      setIsCheckoutProcessing(false);
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto">
      {/* Header with search and cart */}
      <header className="sticky top-0 z-50 bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 px-4 py-3 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1">
            {activeView === "detail" && (
              <button 
                onClick={backToGrid}
                className="text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors duration-200"
              >
                <ChevronRight className="w-5 h-5 rotate-180" />
              </button>
            )}
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search className="w-4 h-4 text-zinc-400" />
              </div>
              <input
                type="search"
                className="w-full h-9 pl-10 pr-4 rounded-md border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-sm placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-shadow duration-200"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="relative p-2 rounded-md text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors duration-200"
            >
              <Filter className="w-5 h-5" />
              {activeFilters.length > 0 && (
                <span className="absolute top-0 right-0 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-[10px] font-medium text-white">
                  {activeFilters.length}
                </span>
              )}
            </button>
            
            <button 
              onClick={() => setIsCartOpen(!isCartOpen)}
              className="relative p-2 rounded-md text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors duration-200"
            >
              <ShoppingCart className="w-5 h-5" />
              {totalItems > 0 && (
                <span className="absolute top-0 right-0 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-[10px] font-medium text-white">
                  {totalItems}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="px-4 relative">
        {/* Filters sidebar */}
        <AnimatePresence>
          {isFilterOpen && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-y-0 left-0 z-40 w-64 bg-white dark:bg-zinc-900 border-r border-zinc-200 dark:border-zinc-800 p-4 overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Filters</h2>
                <button 
                  onClick={() => setIsFilterOpen(false)}
                  className="p-1 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors duration-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium mb-2">Categories</h3>
                  <div className="space-y-2">
                    {categories.map(category => (
                      <label 
                        key={category} 
                        className="flex items-center gap-2 text-sm cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={activeFilters.includes(category)}
                          onChange={() => toggleFilter(category)}
                          className="rounded border-zinc-300 dark:border-zinc-700 text-emerald-500 focus:ring-emerald-500/50 transition-shadow duration-200"
                        />
                        {category}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="mt-6 space-y-2">
                <button 
                  onClick={() => setActiveFilters([])}
                  className="w-full px-4 py-2 text-sm text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-md transition-colors duration-200"
                >
                  Clear all filters
                </button>
                <button 
                  onClick={() => setIsFilterOpen(false)}
                  className="w-full px-4 py-2 text-sm bg-emerald-500 text-white rounded-md hover:bg-emerald-600 transition-colors duration-200"
                >
                  Apply filters
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Cart sidebar */}
        <AnimatePresence>
          {isCartOpen && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-y-0 right-0 z-40 w-full max-w-md bg-white dark:bg-zinc-900 border-l border-zinc-200 dark:border-zinc-800 shadow-xl overflow-hidden flex flex-col"
            >
              <div className="flex items-center justify-between p-4 border-b border-zinc-200 dark:border-zinc-800">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  Cart ({totalItems})
                </h2>
                <button 
                  onClick={() => setIsCartOpen(false)}
                  className="p-1 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors duration-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4">
                {cart.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <ShoppingCart className="w-16 h-16 text-zinc-300 dark:text-zinc-700 mb-4" />
                    <p className="text-zinc-500 dark:text-zinc-400 mb-2">Your cart is empty</p>
                    <p className="text-sm text-zinc-400 dark:text-zinc-500 max-w-xs">
                      Add some products to your cart and they will appear here
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {cart.map((item, index) => (
                      <div 
                        key={`${item.id}-${item.selectedColor}-${item.selectedSize}-${index}`} 
                        className="flex gap-4 p-2 border-b border-zinc-100 dark:border-zinc-800 last:border-0 pb-4"
                      >
                        <div className="relative h-20 w-20 rounded-md overflow-hidden flex-shrink-0 border border-zinc-200 dark:border-zinc-700">
                          <Image 
                            src={item.image}
                            alt={item.name}
                            fill
                            className="object-cover"
                          />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between">
                            <h3 className="text-sm font-medium truncate">{item.name}</h3>
                            <button 
                              onClick={() => removeFromCart(item.id, item.selectedColor, item.selectedSize)}
                              className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          
                          <div className="flex items-center gap-2 mt-1">
                            {item.selectedSize && (
                              <span className="text-xs px-1.5 py-0.5 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400">
                                {item.selectedSize}
                              </span>
                            )}
                            
                            {item.selectedColor && (
                              <span 
                                className="inline-block w-3 h-3 rounded-full border border-zinc-300 dark:border-zinc-600" 
                                style={{ backgroundColor: item.selectedColor }}
                              />
                            )}
                          </div>
                          
                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center border border-zinc-200 dark:border-zinc-700 rounded-md">
                              <button 
                                onClick={() => updateQuantity(item.id, -1, item.selectedColor, item.selectedSize)}
                                className="px-2 py-1 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors duration-200"
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="px-2 py-1 text-sm">{item.quantity}</span>
                              <button 
                                onClick={() => updateQuantity(item.id, 1, item.selectedColor, item.selectedSize)}
                                className="px-2 py-1 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors duration-200"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                            
                            <div className="text-sm font-medium">
                              {formatPrice(item.price * item.quantity)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="p-4 border-t border-zinc-200 dark:border-zinc-800">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-zinc-700 dark:text-zinc-300">Subtotal</span>
                  <span className="text-xl font-semibold">{formatPrice(totalPrice)}</span>
                </div>
                
                <button 
                  onClick={handleCheckout}
                  disabled={cart.length === 0 || isCheckoutProcessing}
                  className={`w-full px-4 py-3 flex items-center justify-center gap-2 rounded-md bg-emerald-500 text-white font-medium transition-all duration-200 ${
                    cart.length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-emerald-600'
                  }`}
                >
                  {isCheckoutProcessing ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Processing...</span>
                    </div>
                  ) : (
                    <>
                      <Lock className="w-4 h-4" />
                      <span>Secure Checkout</span>
                    </>
                  )}
                </button>
                
                <div className="mt-3 flex items-center justify-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
                  <Lock className="w-3 h-3" />
                  <span>Secure checkout with Polar.sh</span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Product grid view */}
        {activeView === "grid" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredProducts.map((product) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="group relative rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 overflow-hidden shadow-sm hover:shadow-md transition-all duration-200"
              >
                <div 
                  className="relative aspect-square cursor-pointer overflow-hidden"
                  onClick={() => viewProductDetails(product)}
                >
                  <Image
                    src={product.image}
                    alt={product.name}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 dark:group-hover:bg-black/20 transition-colors duration-200" />
                  
                  <button 
                    className="absolute top-2 right-2 p-1.5 rounded-full bg-white/80 dark:bg-zinc-800/80 text-zinc-600 dark:text-zinc-400 hover:text-emerald-500 transition-colors duration-200 opacity-0 group-hover:opacity-100"
                    aria-label="Add to wishlist"
                  >
                    <Heart className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="p-3">
                  <div className="flex items-center justify-between mb-1">
                    <h3 
                      className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate cursor-pointer hover:text-emerald-500 transition-colors duration-200"
                      onClick={() => viewProductDetails(product)}
                    >
                      {product.name}
                    </h3>
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400">
                      {product.category}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                      {formatPrice(product.price)}
                    </span>
                    
                    <button
                      onClick={() => addToCart(product)}
                      className="flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 transition-colors duration-200"
                    >
                      <Plus className="w-3 h-3" />
                      Add
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Product detail view */}
        {activeView === "detail" && selectedProduct && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            {/* Product images */}
            <div className="relative aspect-square rounded-xl overflow-hidden border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
              <Image
                src={selectedProduct.image}
                alt={selectedProduct.name}
                fill
                className="object-cover"
              />
              
              {/* Additional images carousel could be added here */}
            </div>
            
            {/* Product info */}
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between">
                  <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">
                    {selectedProduct.name}
                  </h1>
                  <button 
                    className="p-2 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:text-emerald-500 transition-colors duration-200"
                    aria-label="Add to wishlist"
                  >
                    <Heart className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="mt-1 flex items-center">
                  <span className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
                    {formatPrice(selectedProduct.price)}
                  </span>
                  
                  <span className="text-xs px-1.5 py-0.5 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 ml-2">
                    {selectedProduct.category}
                  </span>
                </div>
              </div>
              
              <div className="pt-4 border-t border-zinc-200 dark:border-zinc-800">
                <p className="text-zinc-700 dark:text-zinc-300">
                  {selectedProduct.description || "No description available for this product."}
                </p>
              </div>
              
              {selectedProduct.colors && selectedProduct.colors.length > 0 && (
                <div className="pt-4 border-t border-zinc-200 dark:border-zinc-800">
                  <h2 className="text-sm font-medium mb-3">Color Options</h2>
                  <div className="flex items-center gap-2">
                    {selectedProduct.colors.map(color => (
                      <button
                        key={color}
                        onClick={() => setSelectedColor(color)}
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          selectedColor === color ? 'ring-2 ring-offset-2 ring-emerald-500 dark:ring-offset-zinc-900' : ''
                        }`}
                      >
                        <span
                          className="inline-block w-6 h-6 rounded-full border border-zinc-300 dark:border-zinc-600"
                          style={{ backgroundColor: color }}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedProduct.sizes && selectedProduct.sizes.length > 0 && (
                <div className="pt-4 border-t border-zinc-200 dark:border-zinc-800">
                  <h2 className="text-sm font-medium mb-3">License Options</h2>
                  <div className="flex flex-wrap items-center gap-2">
                    {selectedProduct.sizes.map(size => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className={`px-3 py-1.5 text-sm rounded-md border ${
                          selectedSize === size 
                            ? 'bg-emerald-500 text-white border-emerald-500' 
                            : 'border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:border-emerald-500 hover:text-emerald-500'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="pt-6 border-t border-zinc-200 dark:border-zinc-800">
                <div className="flex items-center gap-4">
                  <div className="flex items-center border border-zinc-200 dark:border-zinc-700 rounded-md">
                    <button 
                      onClick={() => {}}
                      className="px-3 py-2 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors duration-200"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="px-4 py-2 text-sm font-medium">1</span>
                    <button 
                      onClick={() => {}}
                      className="px-3 py-2 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors duration-200"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <button 
                    onClick={() => addToCart(selectedProduct)}
                    className="flex-1 px-4 py-2 flex items-center justify-center gap-2 bg-emerald-500 text-white rounded-md hover:bg-emerald-600 transition-colors duration-200"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    <span>Add to Cart</span>
                  </button>
                </div>
              </div>
              
              <div className="pt-6 border-t border-zinc-200 dark:border-zinc-800 flex items-center">
                <div className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400">
                  <Package className="w-4 h-4" />
                  <span>Instant access after purchase via Polar.sh</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
